/**
 * VideosSection — видео в служебной информации Работы.
 *
 * Работник снимает 30 секунд и голосом объясняет, что не так. Ценность в звуке,
 * поэтому плеер обычный, со звуком, а не немая гифка.
 *
 * ⚠️ Видео НЕ уходит в отчёт клиенту — только служебная информация.
 *
 * Путь файла: presign → PUT напрямую в R2 (минуя наш backend) → confirm →
 * обработка в pm_media (транскод, постер, аудио). Обработка асинхронная, поэтому
 * карточка живёт в трёх состояниях: загрузка → обработка → готово.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, rectSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../api/client';
import { Analytics } from '../analytics';
import { isIosLike, canShareFile, shareFile, downloadBlob } from '../utils/fileShare';

const MAX_VIDEOS = 3;
const MAX_DURATION_SEC = 60;
const MAX_BYTES = 200 * 1024 * 1024;
// Пока хоть одно видео в обработке — переспрашиваем сервер. Пять секунд:
// обработка занимает секунды, а частить незачем.
const POLL_MS = 5000;

// Android сам камеру не предложит: системный пикер там открывает галерею, а на
// перечисление конкретных MIME («video/mp4,video/quicktime») отвечает пустым
// списком. Поэтому на нём показываем свою шторку выбора и просим широкий тип —
// ровно как это уже сделано для фото.
const isAndroid = /android/i.test(navigator.userAgent);

/** Длительность ролика до загрузки — чтобы не гнать по мобильному интернету то,
 *  что сервер всё равно обрежет. Метаданные читаются локально, файл никуда не
 *  уходит. Если браузер не смог — не блокируем: решать будет воркер. */
function readDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(Math.round(v.duration) || null); };
    v.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    v.src = url;
  });
}

/**
 * Иконка видео в стиле бокового меню: 24-я сетка, обводка currentColor,
 * толщина 1.75 — те же параметры, что у остальных иконок навигации, иначе в
 * одном ряду со счётчиком она смотрится инородно.
 *
 * Экспортируется: её же ставит PhotosSection в заголовок аккордеона.
 */
export function VideoIcon({ size = 14, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      style={style} aria-hidden="true">
      <path d="m22 8-6 4 6 4V8Z" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </svg>
  );
}

function fmtDuration(sec) {
  if (!sec && sec !== 0) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * @param onCountChange — сообщает наверх, сколько роликов в Работе. Аккордеон
 *   «Служебная информация» рисует по этому числу иконку со счётчиком и красит
 *   точку: видео грузит этот компонент, и снаружи о них иначе никто не знает.
 */
export default function VideosSection({ taskId, onCountChange }) {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [sheet, setSheet] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const pollRef = useRef(null);
  const failedSeen = useRef(new Set());
  const longPressTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get(`/tasks/${taskId}/videos`);
      const list = Array.isArray(data) ? data : [];
      // Провал обработки виден только здесь: воркер отчитывается на backend, а
      // не в браузер. Без этого события падение pm_media обнаруживается по
      // жалобе тенанта, а не по графику. Трекаем один раз на видео.
      for (const v of list) {
        if (v.status === 'failed' && !failedSeen.current.has(v.id)) {
          failedSeen.current.add(v.id);
          Analytics.videoProcessingFailed(v.error_code || 'unknown');
        }
      }
      setVideos(list);
    } catch {
      // Молча не оставляем: пустой блок читается как «видео нет», хотя оно есть.
      toast.error(t('video.loadFailed'));
    } finally {
      setLoaded(true);
    }
  }, [taskId, t]);

  useEffect(() => { load(); }, [load]);

  // Поллинг только пока что-то обрабатывается. Как только всё готово — таймер
  // снимается сам, иначе карточка Работы будет молотить запросы вечно.
  useEffect(() => {
    const pending = videos.some((v) => v.status === 'processing' || v.status === 'uploading');
    if (!pending) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(load, POLL_MS);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [videos, load]);

  const upload = useCallback(async (file) => {
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast.error(t('video.tooLarge', { mb: Math.round(MAX_BYTES / 1024 / 1024) }));
      return;
    }

    const duration = await readDuration(file);
    if (duration && duration > MAX_DURATION_SEC) {
      // Не отказ: работник уже уехал с объекта, ролик обрежет воркер. Но
      // предупредить обязаны, иначе обрезка выглядит как потеря данных.
      toast(t('video.willTrim', { sec: MAX_DURATION_SEC }));
    }

    // file.type у файла из галереи бывает пустым (заметно на iOS), а presign
    // без типа вернёт отказ. Достраиваем по расширению — и дальше используем
    // ОДНО значение: тип уходит в подпись, и заголовок PUT обязан совпасть с
    // ней байт в байт, иначе R2 отклонит загрузку.
    const contentType = file.type
      || (/\.mov$/i.test(file.name) ? 'video/quicktime' : 'video/mp4');

    setUploading(true);
    setProgress(0);
    Analytics.videoUploadStarted(duration || 0, Math.round(file.size / 1024 / 1024));

    try {
      const { uploadUrl, key } = await api.post(`/tasks/${taskId}/videos/presign`, {
        contentType,
        size: file.size,
        filename: file.name,
      });

      // XHR, а не fetch: нужен прогресс — по мобильному интернету загрузка идёт
      // десятки секунд, и полоса здесь не украшение.
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`HTTP ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('network'));
        xhr.send(file);
      });

      const row = await api.post(`/tasks/${taskId}/videos`, {
        key, size: file.size, filename: file.name, duration_sec: duration || undefined,
      });
      setVideos((prev) => [...prev, row]);
      Analytics.videoUploaded(duration || 0);
    } catch (e) {
      Analytics.videoUploadFailed(e.code || e.message || 'unknown');
      toast.error(e.message || t('video.uploadFailed'));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [taskId, t]);

  // ⚠️ input.click() строго синхронно из обработчика клика: через setTimeout
  // Safari и Chrome теряют user activation и молча не показывают пикер.
  const openPicker = useCallback(() => {
    Analytics.videoPickerOpened();
    // На Android — свой выбор источника: системный диалог там сразу уводит в
    // галерею, и снять видео на месте становится невозможно.
    if (isAndroid) { setSheet(true); return; }
    const input = inputRef.current;
    if (!input) return;
    input.value = '';
    input.click();
  }, []);

  // Шторка закрывается ПОСЛЕ click(): React батчит состояние, а вот потеря
  // user activation здесь фатальна — пикер просто не откроется.
  const openFrom = useCallback((ref, source) => {
    const input = ref.current;
    if (!input) { setSheet(false); return; }
    Analytics.videoPickerSource(source);
    input.value = '';
    input.click();
    setSheet(false);
  }, []);

  const onPicked = useCallback((e) => {
    const file = (e.target.files || [])[0];
    if (file) upload(file);
    setTimeout(() => { try { e.target.value = ''; } catch {} }, 300);
  }, [upload]);

  const remove = useCallback(async (video) => {
    if (!window.confirm(t('video.deleteConfirm'))) return;
    try {
      await api.delete(`/tasks/${taskId}/videos/${video.id}`);
      setVideos((prev) => prev.filter((v) => v.id !== video.id));
      Analytics.videoDeleted(video.status);
    } catch (e) {
      toast.error(e.message || t('video.deleteFailed'));
    }
  }, [taskId, t]);

  // Режим редактирования — как у фото: удержание пальца включает его, карточки
  // начинают покачиваться, появляются крестики и перетаскивание. Отдельной
  // кнопки «править» нет намеренно, жест уже знаком по галерее выше.
  const startLongPress = useCallback(() => {
    if (editMode) return;
    longPressTimer.current = setTimeout(() => {
      setEditMode(true);
      if (navigator.vibrate) navigator.vibrate(40);
    }, 400);
  }, [editMode]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  // Пороги ровно те же, что в галерее фото: вне режима правки перетаскивание
  // заблокировано заведомо недостижимой дистанцией, иначе обычный тап по
  // карточке превращается в микро-драг и видео не запускается.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: editMode ? 5 : 99999 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: editMode ? 0 : 99999, tolerance: 8 } })
  );

  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = videos.findIndex((v) => v.id === active.id);
    const newIndex = videos.findIndex((v) => v.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(videos, oldIndex, newIndex);
    setVideos(reordered);
    Analytics.videoReordered(reordered.length);

    const order = reordered.map((v, i) => ({ id: v.id, display_order: i }));
    api.patch(`/tasks/${taskId}/videos/reorder`, { order })
      .catch(() => toast.error(t('video.reorderFailed')));
  }, [videos, taskId, t]);

  // В просмотрщике листаются только готовые ролики: обрабатываемые и
  // сломанные показывать нечем, и они сбили бы нумерацию «2 из 3».
  const playable = videos.filter((v) => v.status === 'ready' && v.url);

  const liveCount = videos.filter((v) => v.status !== 'failed').length;
  useEffect(() => { onCountChange?.(liveCount); }, [liveCount, onCountChange]);

  const openViewer = useCallback((video) => {
    const i = playable.findIndex((v) => v.id === video.id);
    if (i >= 0) setViewerIndex(i);
  }, [playable]);

  // В режиме правки кнопку добавления прячем: пользователь пришёл двигать и
  // удалять, а пунктирный квадрат в этот момент только сбивает раскладку при
  // перетаскивании — и ловит случайные нажатия соседним пальцем.
  const canAdd = !editMode && videos.filter((v) => v.status !== 'failed').length < MAX_VIDEOS;

  if (!loaded) return null;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{t('video.title')}</span>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          {videos.filter((v) => v.status !== 'failed').length}/{MAX_VIDEOS}
        </span>
        {editMode && (
          <button
            type="button"
            onClick={() => setEditMode(false)}
            data-ph-capture="video_edit_done"
            style={{
              // Прижата к правому краю строки: рядом с крестиком карточки она
              // попадала под тот же палец, которым его закрывают.
              marginLeft: 'auto',
              padding: '2px 10px', borderRadius: 12, background: 'var(--accent)',
              color: 'white', border: 'none', fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}
          >{t('video.done')}</button>
        )}
      </div>

      {/* Сетка в три колонки, а не flex-wrap: лимит как раз три ролика, и они
          должны занимать всю доступную ширину блока — на узком экране
          фиксированные 104 px оставляли пустое поле справа, на широком
          карточки жались в угол. DndContext и SortableContext своих узлов в
          DOM не создают, поэтому карточки остаются прямыми детьми сетки. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={videos.map((v) => v.id)} strategy={rectSortingStrategy}>
            {videos.map((v) => (
              <VideoCard
                key={v.id}
                video={v}
                onDelete={remove}
                t={t}
                editMode={editMode}
                onOpen={openViewer}
                onLongPressStart={startLongPress}
                onLongPressEnd={cancelLongPress}
              />
            ))}
          </SortableContext>
        </DndContext>

        {canAdd && (
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading}
            data-ph-capture="video_add"
            style={{
              width: '100%', aspectRatio: '1 / 1', borderRadius: 10,
              border: '1px dashed var(--border)', background: 'var(--bg2)',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 4, cursor: uploading ? 'default' : 'pointer',
              opacity: uploading ? 0.5 : 1, padding: 4,
            }}
          >
            <VideoIcon size={22} style={{ color: 'var(--text3)' }} />
            <span style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', lineHeight: 1.2 }}>
              {uploading ? `${progress}%` : t('video.add')}
            </span>
          </button>
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
        {t('video.hint', { sec: MAX_DURATION_SEC })}
      </div>

      {viewerIndex !== null && (
        <VideoViewer
          videos={playable}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          taskId={taskId}
          onSummaryApplied={load}
          t={t}
        />
      )}

      {/* accept без capture: работник и снимает на месте, и присылает готовое из
          галереи. Оба сценария живые, навязывать камеру нельзя. */}
      <input
        ref={inputRef}
        type="file"
        accept={isAndroid ? 'video/*' : 'video/mp4,video/quicktime'}
        onChange={onPicked}
        style={{ display: 'none' }}
      />
      {/* capture="environment" открывает съёмку основной камерой сразу */}
      <input
        ref={cameraRef}
        type="file"
        accept="video/*"
        capture="environment"
        onChange={onPicked}
        style={{ display: 'none' }}
      />

      {sheet && (
        <div
          onClick={() => setSheet(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10010,
            background: 'rgba(0,0,0,0.55)', display: 'flex',
            alignItems: 'flex-end', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff', borderRadius: '16px 16px 0 0',
              padding: '16px 0 env(safe-area-inset-bottom, 24px)',
              width: '100%', maxWidth: 480, boxShadow: '0 -4px 24px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 16px' }} />
            <button
              onClick={() => openFrom(cameraRef, 'camera')}
              data-ph-capture="video_picker_camera"
              style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#111111' }}
            >
              <span style={{ fontSize: 22 }}>📹</span>
              {t('photos.camera')}
            </button>
            <button
              onClick={() => openFrom(inputRef, 'gallery')}
              data-ph-capture="video_picker_gallery"
              style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#111111' }}
            >
              <span style={{ fontSize: 22 }}>🖼️</span>
              {t('photos.gallery')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Полноэкранный просмотрщик с переходом между роликами.
 *
 * ⚠️ Почему свой, а не системный. Нативный плеер iOS
 * (video.webkitEnterFullscreen) рисуется системой, и наш DOM внутрь него не
 * попадает — ни стрелок, ни свайпа туда добавить нельзя. Ради навигации по
 * нескольким роликам от него пришлось отказаться: здесь обычный оверлей с
 * playsInline, поверх которого мы уже сами рисуем что нужно.
 *
 * Цена решения: теряются системные плюшки — AirPlay, картинка-в-картинке,
 * выбор скорости. Контролы остаются браузерные и так же гаснут сами.
 */
/**
 * Блок расшифровки под видео.
 *
 * ⚠️ Саммари здесь — ПРЕДЛОЖЕНИЕ, а не запись. В описание Работы оно попадает
 * только по кнопке: из описания растёт смета, а модель умеет звучать гладко и
 * при этом додумывать. Текст можно поправить перед вставкой — правка полезнее
 * отказа, по ней видно, чего модели не хватило.
 */
function VideoSummary({ video, taskId, onApplied, t }) {
  const [text, setText] = useState(video.summary || '');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(video.summary_status);
  const [showTranscript, setShowTranscript] = useState(false);
  // Расшифровка приходит и с сервера в списке, и в ответе на запуск разбора —
  // держим в состоянии, а не правим пропс: мутация объекта из props не
  // перерисовывает и живёт до следующей загрузки списка.
  const [transcript, setTranscript] = useState(video.transcript || '');

  useEffect(() => {
    setText(video.summary || '');
    setStatus(video.summary_status);
    setTranscript(video.transcript || '');
    setShowTranscript(false);
  }, [video]);

  // Пересобрать пересказ по той же расшифровке. Нужно после «Убрать» (передумал)
  // и когда модель отказалась, хотя речь в ролике есть. Распознавание не
  // повторяем — расшифровка объективна, второй прогон дал бы то же за те же
  // деньги.
  const regenerate = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${taskId}/videos/${video.id}/resummarize`);
      setText(r.summary || '');
      setStatus(r.summary_status);
      if (r.transcript) setTranscript(r.transcript);
      Analytics.videoSummaryRegenerated(r.summary_status);
      if (r.summary_status === 'no_speech') toast(t('video.noSpeech'));
    } catch (e) {
      toast.error(e.message || t('video.summaryFailed'));
    } finally {
      setBusy(false);
    }
  }, [taskId, video.id, t]);

  // Кнопка доступна и без расшифровки: именно она запускает распознавание.
  // Автоматически ИИ не включается нигде — платим только за то, что попросили.
  const regenerateBtn = (
    <button
      type="button"
      onClick={regenerate}
      disabled={busy}
      data-ph-capture="video_summary_regenerate"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
        padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
        background: 'rgba(255,255,255,0.12)', color: 'white',
        border: '1px solid rgba(255,255,255,0.25)', fontSize: 13,
        opacity: busy ? 0.5 : 1,
      }}
    >✨ {busy ? t('video.summaryPending') : t('video.summaryCreate')}</button>
  );

  // Сырая расшифровка — по кнопке, не по умолчанию. Саммари это уже пересказ,
  // и показывать оба текста разом значит утроить высоту блока поверх кадра.
  // Но доступ к исходнику нужен: саммари может потерять деталь, а человек,
  // который видео и снимал, по расшифровке сразу поймёт, что именно.
  const transcriptToggle = transcript ? (
    <div>
      <button
        type="button"
        onClick={() => {
          setShowTranscript((v) => !v);
          if (!showTranscript) Analytics.videoTranscriptOpened();
        }}
        data-ph-capture="video_transcript_toggle"
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: 'rgba(255,255,255,0.6)', fontSize: 12, textDecoration: 'underline',
        }}
      >{showTranscript ? t('video.transcriptHide') : t('video.transcriptShow')}</button>
      {showTranscript && (
        <div style={{
          marginTop: 6, fontSize: 13, lineHeight: 1.45,
          color: 'rgba(255,255,255,0.8)', whiteSpace: 'pre-wrap',
        }}>{transcript}</div>
      )}
    </div>
  ) : null;

  const send = useCallback(async (action) => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${taskId}/videos/${video.id}/summary`,
        action === 'accept' ? { action, text } : { action });
      setStatus(action === 'accept' ? 'accepted' : 'rejected');
      Analytics.videoSummaryDecision(action, text !== (video.summary || ''));
      if (action === 'accept') {
        onApplied?.();
        // Описание Работы изменилось на сервере — карточка под просмотрщиком
        // об этом не знает и до перезагрузки страницы показывала бы старый
        // текст. Событие общее, его же шлют TaskModal и список Работ.
        window.dispatchEvent(new Event('tasks-updated'));
      }
    } catch (e) {
      toast.error(e.message || t('video.summaryFailed'));
    } finally {
      setBusy(false);
    }
  }, [taskId, video, text, onApplied, t]);

  // Ждём решения человека. Молчаливого автозапуска нет, поэтому здесь не
  // «идёт обработка», а предложение её начать.
  if (!status || status === 'pending') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {regenerateBtn}
        {transcriptToggle}
      </div>
    );
  }

  // Честнее пустоты: работник снимал ради голоса, и молчание про результат
  // читалось бы как поломка.
  // Дорожки в ролике не было вовсе — разбирать нечего, кнопку не показываем.
  if (status === 'no_audio') {
    return <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>{t('video.noAudio')}</div>;
  }

  if (status === 'no_speech') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>{t('video.noSpeech')}</div>
        {regenerateBtn}
        {transcriptToggle}
      </div>
    );
  }

  // Принятое саммари продолжаем показывать целиком. Убирать текст, оставив одну
  // галочку, — значит заставлять человека закрывать плеер и лезть в описание,
  // чтобы вспомнить, что там оказалось. Искорка помечает, что текст машинный.
  if (status === 'accepted') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 14, lineHeight: 1.45 }}>✨</span>
          <span style={{ color: 'white', fontSize: 14, lineHeight: 1.45 }}>{text}</span>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
          ✓ {t('video.summaryAccepted')}
        </div>
        {transcriptToggle}
      </div>
    );
  }

  // Отклонённое саммари убираем, но и расшифровку, и возможность собрать
  // пересказ заново оставляем: человек отказался от конкретного текста, а не
  // от функции целиком — передумать он вправе.
  if (status === 'rejected') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {regenerateBtn}
        {transcriptToggle}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{t('video.summaryTitle')}</span>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        style={{
          width: '100%', resize: 'vertical', borderRadius: 8, padding: 8,
          fontSize: 14, lineHeight: 1.4, color: 'white',
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button" onClick={() => send('accept')} disabled={busy || !text.trim()}
          data-ph-capture="video_summary_accept"
          style={{
            padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'var(--accent)', color: 'white', fontSize: 13, fontWeight: 600,
            opacity: busy ? 0.5 : 1,
          }}
        >{t('video.summaryAccept')}</button>
        <button
          type="button" onClick={() => send('reject')} disabled={busy}
          data-ph-capture="video_summary_reject"
          style={{
            padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
            background: 'transparent', color: 'rgba(255,255,255,0.7)',
            border: '1px solid rgba(255,255,255,0.25)', fontSize: 13,
          }}
        >{t('video.summaryReject')}</button>
      </div>
      {transcriptToggle}
    </div>
  );
}

function VideoViewer({ videos, index, onIndexChange, onClose, t, taskId, onSummaryApplied }) {
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const touchStartX = useRef(null);
  const controlsTimer = useRef(null);
  // Старт — чистый кадр без панели управления. Ролик короткий, его смотрят
  // целиком, и перемотка нужна редко; браузерные контролы в оверлее (в отличие
  // от системного плеера) сами не гаснут и закрывают нижнюю треть.
  const [showControls, setShowControls] = useState(false);
  const current = videos[index];

  // Тап по кадру — показать панель на пару секунд. Оттуда доступны пауза и
  // перемотка, после чего кадр снова чистый.
  const revealControls = useCallback(() => {
    setShowControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setShowControls(false), 2500);
  }, []);

  useEffect(() => () => { if (controlsTimer.current) clearTimeout(controlsTimer.current); }, []);

  // Вошли ли в настоящий полноэкранный режим. Нужно только ради отступа снизу:
  // Android поверх фуллскрина показывает свою подсказку «To exit full screen…»,
  // и она перекрывает нижнюю часть — ровно там, где у нас расшифровка. Плашка
  // уходит через несколько секунд, но именно эти секунды человек и смотрит на
  // текст. Поднимаем блок над ней.
  const [fullscreen, setFullscreen] = useState(false);

  const [saving, setSaving] = useState(false);
  const [readyFile, setReadyFile] = useState(null);

  /**
   * Скачать ролик на устройство.
   *
   * ⚠️ На iOS атрибут download игнорируется, поэтому файл уходит через
   * navigator.share — и обязательно ДВУМЯ тапами: пока качается blob, user
   * activation от первого тапа протухает, и share падает с NotAllowedError.
   * Тот же двухшаговый приём, что в CustomReportButton.
   *
   * Если fetch не пройдёт (у бакета не настроен CORS для этого происхождения),
   * отдаём ссылку браузеру напрямую — он скачает сам.
   */
  const download = useCallback(async () => {
    if (!current || saving) return;
    setSaving(true);
    const filename = current.filename || `video-${current.id}.mp4`;
    try {
      const res = await fetch(current.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], filename, { type: blob.type || 'video/mp4' });

      if (isIosLike() && canShareFile(file)) {
        setReadyFile(file);           // второй тап — уже с живым жестом
        Analytics.videoDownloaded('share_ready');
      } else {
        downloadBlob(blob, filename);
        Analytics.videoDownloaded('download');
      }
    } catch {
      window.open(current.url, '_blank', 'noopener');
      Analytics.videoDownloaded('fallback_open');
    } finally {
      setSaving(false);
    }
  }, [current, saving]);

  const share = useCallback(async () => {
    if (!readyFile) return;
    const r = await shareFile(readyFile);
    if (r !== 'failed') setReadyFile(null);
    if (r === 'failed') toast.error(t('video.downloadFailed'));
  }, [readyFile, t]);

  // Сменили ролик — предложение «Поделиться» относится уже не к нему.
  useEffect(() => { setReadyFile(null); }, [current]);

  const go = useCallback((delta) => {
    const next = index + delta;
    if (next < 0 || next >= videos.length) return;
    onIndexChange(next);
    Analytics.videoNavigated(delta > 0 ? 'next' : 'prev');
  }, [index, videos.length, onIndexChange]);

  // Смена ролика: меняем src на том же элементе и запускаем воспроизведение
  // вручную. Так надёжнее пересоздания элемента — оно на iOS роняет право на
  // автозапуск, полученное первым нажатием, и следующий ролик открывался бы
  // молчащим стоп-кадром.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !current) return;
    v.src = current.url;
    setShowControls(false);   // новый ролик — снова чистый кадр
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }, [current]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  // Пока открыт просмотрщик — блокируем прокрутку страницы под ним и убираем
  // плавающую кнопку «+», иначе она перекрывает стрелку и ловит свайп.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => {
      document.body.style.overflow = prev;
      window.dispatchEvent(new CustomEvent('fab-show'));
    };
  }, []);

  /**
   * Настоящий полноэкранный режим — чтобы уехала и адресная строка браузера.
   *
   * ⚠️ На iPhone в Safari это невозможно в принципе: requestFullscreen там
   * поддерживается только у самого <video>, произвольный элемент в полный
   * экран не увести. Поэтому вызов обёрнут и молча отваливается — оверлей и
   * так занимает всё окно, просто под браузерным UI. В PWA с домашнего экрана
   * этого UI нет, и разницы не видно.
   *
   * Работает на Android и десктопе. Запрос идёт сразу после клика, пока жива
   * transient activation от него.
   */
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !el.requestFullscreen) return;
    let ours = false;
    el.requestFullscreen({ navigationUI: 'hide' })
      .then(() => { ours = true; setFullscreen(true); })
      .catch(() => {});
    return () => {
      // Выходим только если в полноэкранный режим нас увёл этот просмотрщик.
      if (ours && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  if (!current) return null;

  const arrow = (dir) => ({
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    [dir === 'prev' ? 'left' : 'right']: 8,
    width: 44, height: 44, borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)', color: 'white',
    border: 'none', fontSize: 24, cursor: 'pointer', zIndex: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    WebkitUserSelect: 'none', userSelect: 'none', WebkitTapHighlightColor: 'transparent',
  });

  return (
    <div
      ref={rootRef}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        touchStartX.current = null;
        if (dx < -50) go(1);
        else if (dx > 50) go(-1);
      }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.95)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <video
        ref={videoRef}
        poster={current.poster_url}
        controls={showControls}
        autoPlay
        playsInline
        onClick={revealControls}
        style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }}
      />

      {/* Расшифровка — под кадром, поверх затемнения. Ради неё мы и отказались
          от системного плеера: внутрь него свой блок не поставить. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          padding: fullscreen
            ? '14px 16px calc(64px + env(safe-area-inset-bottom))'
            : '14px 16px calc(14px + env(safe-area-inset-bottom))',
          background: 'linear-gradient(transparent, rgba(0,0,0,0.85) 30%)',
          maxHeight: '45%', overflowY: 'auto', zIndex: 3,
        }}
      >
        <VideoSummary video={current} taskId={taskId} onApplied={onSummaryApplied} t={t} />
      </div>

      {videos.length > 1 && (
        <>
          <span style={{
            position: 'absolute', top: 14, left: 0, right: 0, textAlign: 'center',
            color: 'rgba(255,255,255,0.75)', fontSize: 13, pointerEvents: 'none',
          }}>{index + 1} / {videos.length}</span>

          {index > 0 && (
            <button type="button" onClick={() => go(-1)} data-ph-capture="video_prev"
              title={t('video.prev')} style={arrow('prev')}>‹</button>
          )}
          {index < videos.length - 1 && (
            <button type="button" onClick={() => go(1)} data-ph-capture="video_next"
              title={t('video.next')} style={arrow('next')}>›</button>
          )}
        </>
      )}

      {/* Скачивание — здесь, а не на карточке: действие редкое, а в сетке из
          трёх плиток лишняя кнопка съедает место и ловит случайные нажатия.
          В просмотрщике пользователь уже выбрал конкретный ролик. */}
      <button
        type="button"
        onClick={readyFile ? share : download}
        disabled={saving}
        data-ph-capture="video_download"
        title={readyFile ? t('video.share') : t('video.download')}
        style={{
          position: 'absolute', top: 10, left: 10, height: 36,
          padding: readyFile ? '0 14px' : 0, width: readyFile ? 'auto' : 36,
          borderRadius: 18, background: readyFile ? 'var(--accent)' : 'rgba(255,255,255,0.15)',
          color: 'white', border: 'none', fontSize: 13, cursor: 'pointer', zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          opacity: saving ? 0.5 : 1,
          WebkitUserSelect: 'none', userSelect: 'none', WebkitTapHighlightColor: 'transparent',
        }}
      >
        {readyFile ? t('video.share') : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        )}
      </button>

      <button
        type="button"
        onClick={onClose}
        data-ph-capture="video_viewer_close"
        title={t('video.close')}
        style={{
          position: 'absolute', top: 10, right: 10, width: 36, height: 36,
          borderRadius: '50%', background: 'rgba(255,255,255,0.15)', color: 'white',
          border: 'none', fontSize: 18, cursor: 'pointer', zIndex: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          WebkitUserSelect: 'none', userSelect: 'none', WebkitTapHighlightColor: 'transparent',
        }}
      >×</button>
    </div>
  );
}

/**
 * Карточка одного видео. Вынесена на верхний уровень модуля намеренно:
 * компоненты внутри компонентов пересоздаются на каждый рендер и роняют
 * состояние плеера (видео перезапускается на середине).
 */
function VideoCard({ video, onDelete, t, editMode, onOpen, onLongPressStart, onLongPressEnd }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: video.id });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
    WebkitTouchCallout: 'none',
    userSelect: 'none',
  };

  // Обёртка одна на все состояния карточки: тянуть можно и обрабатываемое, и
  // сломанное видео — порядок от статуса не зависит.
  //
  // ⚠️ Крестик рендерится СНАРУЖИ перетаскиваемого элемента, во внешнем
  // relative-контейнере. Внутри него нажатие перехватывает сенсор dnd-kit:
  // палец опускается на кнопку, сенсор считает это началом перетаскивания, и
  // click до кнопки уже не доходит — она выглядит мёртвой. В галерее фото
  // крестик вынесен ровно по этой причине.
  const wrap = (inner, extra = null) => (
    <div style={{ position: 'relative' }}>
      <div
        ref={setNodeRef}
        style={sortableStyle}
        {...attributes}
        {...(editMode ? listeners : {})}
        onPointerDown={onLongPressStart}
        onPointerUp={onLongPressEnd}
        onPointerLeave={onLongPressEnd}
        className={editMode && !isDragging ? 'photo-wiggle' : ''}
      >{inner}</div>
      {extra}
    </div>
  );

  // Карточка только открывает просмотрщик — воспроизведением и полноэкранным
  // режимом занимается он. Раньше здесь жил вызов нативного плеера iOS, но в
  // нём нельзя нарисовать переход между роликами: системный UI наш DOM не
  // пускает.
  const openViewer = useCallback(() => {
    Analytics.videoPlayed(video.duration_sec || 0);
    onOpen(video);
  }, [video, onOpen]);

  // ⚠️ Запрет выделения нужен на КАЖДОМ слое, а не только на обёртке. При
  // удержании iOS иначе выделяет символ ▶ внутри кнопки и поднимает системное
  // меню «Копировать/Вставить» — поверх нашего режима правки. Наследования тут
  // не хватает: Safari трактует содержимое кнопки отдельно.
  const noSelect = {
    WebkitUserSelect: 'none',
    userSelect: 'none',
    WebkitTouchCallout: 'none',
    WebkitTapHighlightColor: 'transparent',
  };

  // Карточка тянется на всю колонку сетки и держит квадрат через aspect-ratio:
  // ширину задаёт родитель, высота считается от неё.
  const box = {
    width: '100%', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden',
    position: 'relative', background: 'var(--bg2)', border: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    ...noSelect,
  };

  // Крестик — только в режиме правки, как у фото: постоянный крестик на
  // карточке провоцирует случайное удаление пальцем на телефоне.
  const deleteBtn = editMode ? (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onDelete(video); }}
      data-ph-capture="video_delete"
      title={t('video.delete')}
      style={{
        position: 'absolute', right: 2, top: 2, zIndex: 20,
        width: 20, height: 20, borderRadius: '50%',
        background: 'rgba(0,0,0,0.65)', color: 'white', border: 'none',
        fontSize: 13, lineHeight: 1, cursor: 'pointer', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...noSelect,
      }}
    >×</button>
  ) : null;

  if (video.status === 'processing' || video.status === 'uploading') {
    // Обработка идёт секунды, но если воркер недоступен, карточка застревает
    // тут надолго — убрать её должно быть можно, поэтому удаление доступно во
    // всех состояниях, а не только у готового видео.
    return wrap(
      <div style={box}>
        <div style={{ textAlign: 'center', padding: 6 }}>
          <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2, margin: '0 auto 6px' }} />
          <div style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.2 }}>{t('video.processing')}</div>
        </div>
      </div>,
      deleteBtn
    );
  }

  if (video.status === 'failed') {
    // Здесь кнопка удаления текстом и вне режима правки: сломанную карточку
    // нужно убрать сразу, искать жест удержания ради мусора незачем.
    return wrap(
      <div style={{ ...box, flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 16 }}>⚠️</span>
        <div style={{ fontSize: 10, color: 'var(--text3)', textAlign: 'center', padding: '0 6px', lineHeight: 1.2 }}>
          {t('video.failed')}
        </div>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete(video); }}
          data-ph-capture="video_delete_failed"
          style={{
            fontSize: 10, color: 'var(--red)', background: 'none',
            border: 'none', cursor: 'pointer', ...noSelect,
          }}
        >{t('video.delete')}</button>
      </div>
    );
  }

  // Постер продублирован фоном контейнера: пока плеер тянет первый кадр (а на
  // мобильной сети это заметно), под ним уже лежит картинка, и карточка не
  // мигает серым прямоугольником.
  return wrap(
    <div style={video.poster_url
      ? { ...box, backgroundImage: `url(${video.poster_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : box}>
      {/* Превью — обычная картинка постера. Элемент <video> в карточке больше
          не нужен: воспроизведение целиком у просмотрщика, а картинка не тянет
          за собой ни системное меню по удержанию, ни лишний медиаслой. */}
      {video.poster_url && (
        <img
          src={video.poster_url}
          alt=""
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            display: 'block', pointerEvents: 'none', ...noSelect,
          }}
        />
      )}
      {/* В режиме правки кнопка воспроизведения не срабатывает: там палец
          таскает карточку, и запускать плеер посреди перетаскивания незачем. */}
      <button
        type="button"
        onClick={editMode ? undefined : openViewer}
        data-ph-capture="video_play"
        title={t('video.play')}
        style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'rgba(0,0,0,0.25)',
          border: 'none', cursor: editMode ? 'grab' : 'pointer', color: 'white',
          fontSize: 26, opacity: editMode ? 0.35 : 1,
          ...noSelect,
        }}
      >▶</button>
      {!!video.duration_sec && (
        <span style={{
          position: 'absolute', right: 4, bottom: 4, fontSize: 10,
          background: 'rgba(0,0,0,0.6)', color: 'white', padding: '1px 5px', borderRadius: 6,
        }}>{fmtDuration(video.duration_sec)}</span>
      )}
    </div>,
    deleteBtn
  );
}
